// ─────────────────────────────────────────────────────────────────────────────
// StudentDetail.jsx
// A single student's full report: (1) profile and (2) fee history (assignments
// + recorded payments). Reused by Student Reports and Passed Out Students.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useMemo, useRef } from "react";
import { C, fmt, fmtClass, fmtSection, Card, CardHeader, TH, TD, Badge } from "../fees/feeUI";
import {
  statusColor, fmtDate, loadFeeAssignments, loadPayments, loadPrevDues,
  StatCard, StatGrid, BackButton, ReportToolbar,
} from "./reportsShared";
import { printReport, exportCsv, deriveCampus } from "./reportExport";

// A labelled value used in the profile grid
const Info = ({ label, value, badge, color }) => (
  <div style={{ padding: "12px 0", borderBottom: `1px dashed ${C.border}` }}>
    <div style={{ fontSize: "10.5px", fontWeight: 700, color: C.slate, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "4px" }}>{label}</div>
    {badge
      ? <Badge color={color || "slate"}>{value}</Badge>
      : <div style={{ fontSize: "13.5px", fontWeight: 600, color: value && value !== "—" ? C.text : C.slateLight }}>{value || "—"}</div>}
  </div>
);

// ── Print button component ──────────────────────────────────────────────────
const PrintButton = ({ onClick, label = "Print" }) => (
  <button
    onClick={onClick}
    style={{
      padding: "8px 16px",
      borderRadius: "8px",
      border: `1.5px solid ${C.navy}`,
      background: C.white,
      color: C.navy,
      fontSize: "12px",
      fontWeight: 600,
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      transition: "all 0.15s",
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.background = C.navy;
      e.currentTarget.style.color = "#fff";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.background = C.white;
      e.currentTarget.style.color = C.navy;
    }}
  >
    🖨 {label}
  </button>
);

const StudentDetail = ({ student, onBack, campus, backLabel = "Back to students" }) => {
  // Fee data is read once when a student is opened.
  const { assignments, payments, totalAssigned, totalPaid, balance, prevDues } = useMemo(() => {
    const feeData = loadFeeAssignments();
    const allPays = loadPayments();
    const asg = Object.values(feeData[String(student.id)] || {});
    const pays = allPays.filter(p => Number(p.studentId) === Number(student.id))
      .sort((a, b) => new Date(b.date) - new Date(a.date));
    const assigned = asg.reduce((s, a) => s + (a.netPayable || 0), 0);
    const statusPaid = asg.filter(a => a.status === "Paid" || a.status === "Waived").reduce((s, a) => s + (a.netPayable || 0), 0);
    const historyPaid = pays.reduce((s, p) => s + (p.amount || 0), 0);
    const paid = Math.max(statusPaid, historyPaid);
    const dues = loadPrevDues(student.id);
    return { assignments: asg, payments: pays, totalAssigned: assigned, totalPaid: paid, balance: assigned - paid + dues, prevDues: dues };
  }, [student.id]);

  // Build profile data - includes withdrawal/passed out info
  const profile = [
    ["Student ID",        student.rollNo,                  false],
    ["Full Name",         student.name,                    false],
    ["Gender",            student.gender,                  false],
    ["Date of Birth",     fmtDate(student.date_of_birth),  false],
    ["Campus",            student.campus_name,             false],
    ["Class",             fmtClass(student.class),         false],
    ["Section",           fmtSection(student.section),     false],
    ["Academic Session",  student.academic_session,        false],
    ["Admission Date",    fmtDate(student.admission_date), false],
    ["Status",            student.status,                  "badge", statusColor(student.status)],
    ["Fee Plan",          student.fee_plan_name,           false],
    ["Scholarship",       student.scholarship_name,        false],
    ["Fee Status",        student.fee_status,              "badge", (student.fee_status === "Paid" ? "green" : "amber")],
    ["Registered Fee",    student.actual_fee_amount != null ? fmt(Number(student.actual_fee_amount)) : "—", false],
  ];

  // Add withdrawal/passed out date if available
  const withdrawalDate = student.withdrawal_date || student.passed_out_date;
  if (withdrawalDate) {
    profile.push(["Passed Out / Withdrawal Date", fmtDate(withdrawalDate), false]);
  }
  if (student.withdrawal_reason) {
    profile.push(["Withdrawal Reason", student.withdrawal_reason, false]);
  }

  const reportCampus = campus || deriveCampus([student]);
  
  // ── Single-student report for printing ─────────────────────────────────────
  const printReportConfig = () => {
    // For print: filter payments to show only dues (unpaid or partial)
    const duePayments = payments.filter(p => p.status !== "Paid" && p.status !== "paid" && p.status !== "Waived");
    
    // Filter assignments to only show those with amount > 0
    const activeAssignments = assignments.filter(a => (a.netPayable || 0) > 0);

    // Build profile pairs for the report
    const profilePairs = profile.map(([label, value]) => [label, value]);

    const sections = [];

    // Profile section
    sections.push({
      heading: "Profile",
      pairs: profilePairs,
    });

    // Fee Assignments section - only if there are active assignments
    if (activeAssignments.length > 0) {
      sections.push({
        heading: "Fee Assignments",
        headers: ["Fee Category", "Month", "Session", "Net Payable", "Status"],
        rightCols: ["Net Payable"],
        rows: activeAssignments.map(a => [
          a.categoryLabel || a.categoryId,
          a.month || "—",
          a.session || "—",
          fmt(a.netPayable),
          a.status || "Unpaid",
        ]),
      });
    }

    // Payment History section - only show due payments
    if (duePayments.length > 0) {
      sections.push({
        heading: "Payment History (Dues Only)",
        headers: ["#", "Date", "Amount", "Method", "Category", "Remarks"],
        rightCols: ["Amount"],
        rows: duePayments.map((p, i) => [
          i + 1,
          fmtDate(p.date),
          fmt(p.amount),
          p.method || "Cash",
          p.feeCategory || "—",
          p.remarks || "—",
        ]),
      });
    }

    // If no due payments but there are assignments, show paid status
    if (duePayments.length === 0 && activeAssignments.length > 0) {
      sections.push({
        heading: "Payment Status",
        pairs: [["All fees have been paid.", ""]],
      });
    }

    // If no fee assignments at all
    if (activeAssignments.length === 0 && duePayments.length === 0) {
      sections.push({
        heading: "Fee Information",
        pairs: [["No fees assigned to this student.", ""]],
      });
    }

    return {
      title: `Student Report — ${student.name}`,
      campus: reportCampus,
      scopeMeta: [
        ["Student ID", student.rollNo],
        ["Class", fmtClass(student.class)],
        ["Section", fmtSection(student.section)],
        ["Status", student.status],
      ],
      // No summary cards (Total Assigned, Total Paid, Previous Dues, Balance) in print
      summaryCards: [],
      sections: sections,
    };
  };

  // ── Print Fee Assignments only ────────────────────────────────────────────
  const printFeeAssignments = () => {
    const activeAssignments = assignments.filter(a => (a.netPayable || 0) > 0);
    
    const sections = [];
    
    // Student Information - organized in a clean grid
    sections.push({
      heading: "Student Information",
      pairs: [
        ["Student ID", student.rollNo],
        ["Name", student.name],
        ["Class", fmtClass(student.class)],
        ["Section", fmtSection(student.section)],
        ["Academic Session", student.academic_session || "—"],
        ["Status", student.status],
      ],
    });

    // Fee Assignments Summary
    sections.push({
      heading: "Fee Summary",
      pairs: [
        ["Total Assigned", fmt(totalAssigned)],
        ["Total Paid", fmt(totalPaid)],
        ["Balance", fmt(balance)],
      ],
    });

    // Fee Assignments Table
    if (activeAssignments.length > 0) {
      sections.push({
        heading: "Fee Assignments",
        headers: ["#", "Fee Category", "Month", "Session", "Net Payable", "Status"],
        rightCols: ["Net Payable"],
        rows: activeAssignments.map((a, i) => [
          i + 1,
          a.categoryLabel || a.categoryId,
          a.month || "—",
          a.session || "—",
          fmt(a.netPayable),
          a.status || "Unpaid",
        ]),
      });
    } else {
      sections.push({
        heading: "Fee Assignments",
        pairs: [["No fees assigned to this student.", ""]],
      });
    }

    printReport({
      title: `Fee Assignments Report — ${student.name}`,
      campus: reportCampus,
      scopeMeta: [
        ["Student ID", student.rollNo],
        ["Class", fmtClass(student.class)],
        ["Section", fmtSection(student.section)],
        ["Total Assignments", activeAssignments.length],
      ],
      summaryCards: [],
      sections: sections,
    });
  };

  // ── Print Payment History only ────────────────────────────────────────────
  const printPaymentHistory = () => {
    const duePayments = payments.filter(p => p.status !== "Paid" && p.status !== "paid" && p.status !== "Waived");
    
    const sections = [];
    
    // Student Information - organized in a clean grid
    sections.push({
      heading: "Student Information",
      pairs: [
        ["Student ID", student.rollNo],
        ["Name", student.name],
        ["Class", fmtClass(student.class)],
        ["Section", fmtSection(student.section)],
        ["Academic Session", student.academic_session || "—"],
        ["Status", student.status],
      ],
    });

    // Payment Summary
    sections.push({
      heading: "Payment Summary",
      pairs: [
        ["Total Assigned", fmt(totalAssigned)],
        ["Total Paid", fmt(totalPaid)],
        ["Balance", fmt(balance)],
        ["Outstanding Dues", duePayments.length],
      ],
    });

    // Payment History Table - showing only dues
    if (duePayments.length > 0) {
      sections.push({
        heading: "Outstanding Dues / Payment History",
        headers: ["#", "Date", "Amount", "Method", "Category", "Remarks"],
        rightCols: ["Amount"],
        rows: duePayments.map((p, i) => [
          i + 1,
          fmtDate(p.date),
          fmt(p.amount),
          p.method || "Cash",
          p.feeCategory || "—",
          p.remarks || "—",
        ]),
      });
    } else {
      sections.push({
        heading: "Payment Status",
        pairs: [["All fees have been paid. No outstanding dues.", ""]],
      });
    }

    printReport({
      title: `Payment History Report — ${student.name}`,
      campus: reportCampus,
      scopeMeta: [
        ["Student ID", student.rollNo],
        ["Class", fmtClass(student.class)],
        ["Section", fmtSection(student.section)],
        ["Total Payments", payments.length],
        ["Outstanding Dues", duePayments.length],
      ],
      summaryCards: [],
      sections: sections,
    });
  };

  // ── CSV report config (keeps full data) ───────────────────────────────────
  const csvReportConfig = () => ({
    title: `Student Report — ${student.name}`,
    campus: reportCampus,
    scopeMeta: [
      ["Student ID", student.rollNo],
      ["Class", fmtClass(student.class)],
      ["Section", fmtSection(student.section)],
      ["Status", student.status],
    ],
    summaryCards: [
      { label: "Total Assigned", value: fmt(totalAssigned) },
      { label: "Total Paid", value: fmt(totalPaid) },
      { label: "Previous Dues", value: fmt(prevDues) },
      { label: "Balance", value: fmt(balance) },
    ],
    sections: [
      { heading: "Profile", pairs: profile.map(([label, value]) => [label, value]) },
      {
        heading: "Fee Assignments",
        headers: ["Fee Category", "Month", "Session", "Net Payable", "Status"],
        rightCols: ["Net Payable"],
        rows: assignments.map(a => [a.categoryLabel || a.categoryId, a.month || "—", a.session || "—", fmt(a.netPayable), a.status || "Unpaid"]),
      },
      {
        heading: "Payment History",
        headers: ["#", "Date", "Amount", "Method", "Category", "Remarks"],
        rightCols: ["Amount"],
        rows: payments.map((p, i) => [i + 1, fmtDate(p.date), fmt(p.amount), p.method || "Cash", p.feeCategory || "—", p.remarks || "—"]),
      },
    ],
  });

  const safeName = (student.name || "student").replace(/\s+/g, "-").toLowerCase();

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
        <BackButton onClick={onBack} label={backLabel} />
        <div style={{ marginBottom: "16px" }}>
          <ReportToolbar
            onExport={() => exportCsv({ filename: `student-${safeName}.csv`, ...csvReportConfig() })}
            onPrint={() => printReport(printReportConfig())}
          />
        </div>
      </div>

      {/* Identity banner */}
      <Card style={{ marginBottom: "20px" }}>
        <div style={{ padding: "20px 22px", display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
          <div style={{ width: 56, height: 56, borderRadius: "14px", background: C.navy, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: "22px", flexShrink: 0 }}>
            {(student.name || "?").charAt(0).toUpperCase()}
          </div>
          <div>
            <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: "20px", fontWeight: 700, color: C.text }}>{student.name}</div>
            <div style={{ fontSize: "12.5px", color: C.slate, marginTop: "3px", display: "flex", gap: "16px", flexWrap: "wrap" }}>
              <span>ID: <strong style={{ color: C.text }}>{student.rollNo}</strong></span>
              <span>Class: <strong style={{ color: C.text }}>{fmtClass(student.class)}</strong></span>
              <span>Section: <strong style={{ color: C.text }}>{fmtSection(student.section)}</strong></span>
            </div>
          </div>
          <div style={{ marginLeft: "auto" }}><Badge color={statusColor(student.status)}>{student.status}</Badge></div>
        </div>
      </Card>

      {/* 1 — Profile report */}
      <Card style={{ marginBottom: "20px" }}>
        <CardHeader title="Profile Report" />
        <div style={{ padding: "8px 22px 18px", display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: "0 28px" }}>
          {profile.map(([label, value, badge, color]) => (
            <Info key={label} label={label} value={value} badge={badge === "badge"} color={color} />
          ))}
        </div>
      </Card>

      {/* 2 — Fee history summary cards */}
      <StatGrid>
        <StatCard label="Total Assigned" value={fmt(totalAssigned)} accent={C.navy} />
        <StatCard label="Total Paid"     value={fmt(totalPaid)}     accent={C.green} />
        <StatCard label="Previous Dues"  value={fmt(prevDues)}      accent={prevDues > 0 ? C.amber : C.slateLight} />
        <StatCard label="Balance"        value={fmt(balance)}       accent={balance > 0 ? C.red : C.green} />
      </StatGrid>

      {/* 3 — Fee Assignments with print button */}
      <Card style={{ marginBottom: "20px" }}>
        <CardHeader 
          title="Fee Assignments" 
          right={
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <Badge color="slate">{assignments.length}</Badge>
              <PrintButton onClick={printFeeAssignments} label="Print Fee Assignments" />
            </div>
          } 
        />
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr><TH>Fee Category</TH><TH>Month</TH><TH>Session</TH><TH right>Net Payable</TH><TH>Status</TH></tr></thead>
            <tbody>
              {assignments.length === 0 ? (
                <tr><td colSpan={5} style={{ padding: "40px", textAlign: "center", color: C.slate, fontSize: "13px" }}>No fees assigned to this student.</td></tr>
              ) : assignments.map((a, i) => (
                <tr key={a.entryKey || `${a.categoryId}_${a.month}_${i}`} style={{ background: i % 2 === 0 ? C.white : C.bg }}>
                  <TD><Badge color="amber">{a.categoryLabel || a.categoryId}</Badge></TD>
                  <TD>{a.month || "—"}</TD>
                  <TD>{a.session || "—"}</TD>
                  <TD right mono>{fmt(a.netPayable)}</TD>
                  <TD><Badge color={a.status === "Paid" ? "green" : a.status === "Waived" ? "blue" : "red"}>{a.status || "Unpaid"}</Badge></TD>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* 4 — Payment History with print button */}
      <Card>
        <CardHeader 
          title="Payment History" 
          right={
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <Badge color="slate">{payments.length}</Badge>
              <PrintButton onClick={printPaymentHistory} label="Print Payment History" />
            </div>
          } 
        />
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr><TH>#</TH><TH>Date</TH><TH right>Amount</TH><TH>Method</TH><TH>Category</TH><TH>Remarks</TH></tr></thead>
            <tbody>
              {payments.length === 0 ? (
                <tr><td colSpan={6} style={{ padding: "40px", textAlign: "center", color: C.slate, fontSize: "13px" }}>No payments recorded for this student.</td></tr>
              ) : payments.map((p, i) => (
                <tr key={p.id || i} style={{ background: i % 2 === 0 ? C.white : C.bg }}>
                  <TD><span style={{ color: C.slateLight, fontWeight: 600 }}>{i + 1}</span></TD>
                  <TD>{fmtDate(p.date)}</TD>
                  <TD right mono><span style={{ color: C.green, fontWeight: 700 }}>{fmt(p.amount)}</span></TD>
                  <TD><Badge color="blue">{p.method || "Cash"}</Badge></TD>
                  <TD>{p.feeCategory || "—"}</TD>
                  <TD><span style={{ color: C.slate, fontSize: "12px" }}>{p.remarks || "—"}</span></TD>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default StudentDetail;